import { useState, useEffect, useMemo } from 'react';
import { fetchAPI } from '../../utils/utils';
import UserInfo from '../login/UserInfo';
import HeaderTabs from '../interface/HeaderTabs';
import Pressable from '../other/Pressable';
import SubvencionsCalendar from './SubvencionsCalendar';
import SubvencionsGrantCard from './SubvencionsGrantCard';
import SubvencionsGrantDetailPage from './SubvencionsGrantDetailPage';
import { getSubvencionsRoute } from './subvencionsRouting';

const STATUS_ORDER = { open: 0, pending: 1, closed: 2, waiting: 3, unknown: 4 };

const GRANT_PALETTE = [
    '#4285F4', '#EA4335', '#34A853', '#FBBC04', '#FF6D01',
    '#46BDC6', '#7B1FA2', '#E91E63', '#00ACC1', '#FF7043',
    '#5C6BC0', '#8D6E63', '#26A69A', '#D81B60', '#1E88E5',
    '#43A047', '#F4511E', '#6D4C41', '#00897B', '#C0CA33',
];

function SubvencionsApp({ userInfo, castellersInfo, ...props }) {
    const {
        detailSlug,
        detailYear,
        isDetailView,
    } = getSubvencionsRoute();
    const [grants, setGrants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedGrant, setSelectedGrant] = useState(null);
    const [fetchedAt, setFetchedAt] = useState(null);
    const [showClosed, setShowClosed] = useState(false);
    const [showWaiting, setShowWaiting] = useState(false);

    useEffect(() => {
        if (isDetailView) {
            return;
        }

        setLoading(true);
        setError(null);

        fetchAPI('/subvencions/cultura', (data) => {
            if (data && data.items) {
                const normalized = data.items.map(g =>
                    g.status === 'pending' && !g.openAt && !g.closeAt
                        ? { ...g, status: 'waiting' }
                        : g
                );
                setGrants(normalized);
                setFetchedAt(data.fetchedAt);

                const now = new Date();
                const openingSoonSlugs = normalized
                    .filter(g => {
                        if (g.status !== 'pending' || !g.openAt) return false;
                        const diff = Math.ceil((new Date(g.openAt) - now) / (1000 * 60 * 60 * 24));
                        return diff > 0 && diff <= 15;
                    })
                    .map(g => g.slug);

                try {
                    localStorage.setItem('subvencions_seen_opening_soon', JSON.stringify(openingSoonSlugs));
                } catch { /* storage full or blocked */ }
            } else if (data && data.error) {
                setError(data.error);
            }
            setLoading(false);
        }, false);
    }, [isDetailView]);

    const grantColors = useMemo(() => {
        const map = {};
        grants.forEach((g, i) => { map[g.slug] = GRANT_PALETTE[i % GRANT_PALETTE.length]; });
        return map;
    }, [grants]);

    const sortedGrants = [...grants].sort((a, b) => {
        const orderDiff = (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3);
        if (orderDiff !== 0) return orderDiff;
        if (a.status === 'closed') return new Date(b.closeAt) - new Date(a.closeAt);
        return new Date(a.openAt) - new Date(b.openAt);
    });

    const visibleGrants = sortedGrants.filter(g => {
        if (g.status === 'closed' && !showClosed) return false;
        if (g.status === 'waiting' && !showWaiting) return false;
        return true;
    });

    const closedCount = grants.filter(g => g.status === 'closed').length;
    const waitingCount = grants.filter(g => g.status === 'waiting').length;

    const handleSelectGrant = (grant) => {
        setSelectedGrant(prev => prev?.slug === grant.slug ? null : grant);

        setTimeout(() => {
            const el = document.getElementById(`subvencions-grant-${grant.slug}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    return (
        <div style={{
            padding: 30,
            maxWidth: '90%',
            margin: '0 auto',
            boxSizing: 'border-box',
            minHeight: 500,
        }}>
            <UserInfo
                userInfo={userInfo}
                castellersInfo={castellersInfo}
                {...props}
            />
            <HeaderTabs userInfo={userInfo} {...props} />
            <Pressable
                style={{ backgroundColor: '#eee' }}
                className="boto-back"
                href={isDetailView ? '/subvencions' : '/'}
            >
                {isDetailView ? '← Tornar a subvencions' : '← Tornar a la pàgina principal'}
            </Pressable>

            {isDetailView ? null : (
                <>
                    <h2 style={{ marginTop: 30, color: '#333', marginBottom: 8 }}>
                        🏛️ Subvencions de cultura
                    </h2>
                    <p style={{ color: '#666', fontSize: 14, marginTop: 0, marginBottom: 24 }}>
                        Calendari de convocatòries de subvencions de cultura d'administracions públiques
                    </p>

                    <div style={{
                        display: 'flex',
                        gap: 12,
                        marginBottom: 20,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        fontSize: 13,
                        color: '#888',
                    }}>
                        <span>● Oberta</span>
                        <span>◐ Pendent d'obrir</span>
                        <span>◌ A l'espera</span>
                        <span style={{ opacity: 0.6 }}>○ Fora de termini</span>
                    </div>
                </>
            )}

            {isDetailView ? (
                <div style={{ marginTop: 24 }}>
                    <SubvencionsGrantDetailPage
                        slug={detailSlug}
                        year={detailYear}
                    />
                </div>
            ) : loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
                    <div style={{
                        width: 32, height: 32,
                        border: '3px solid #e0e0e0',
                        borderTopColor: '#1976d2',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 12px',
                    }} />
                    Carregant subvencions...
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            ) : error ? (
                <div style={{
                    textAlign: 'center',
                    padding: 40,
                    color: '#d32f2f',
                    backgroundColor: '#fce4ec',
                    borderRadius: 8,
                }}>
                    {error}
                </div>
            ) : (
                <>
                    <SubvencionsCalendar
                        grants={grants}
                        grantColors={grantColors}
                        selectedGrant={selectedGrant}
                        onSelectGrant={handleSelectGrant}
                    />

                    <div style={{ marginTop: 32 }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 16,
                            flexWrap: 'wrap',
                            gap: 8,
                        }}>
                            <h3 style={{ color: '#333', margin: 0 }}>
                                Subvencions ({visibleGrants.length})
                            </h3>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {waitingCount > 0 ? (
                                    <button
                                        onClick={() => setShowWaiting(!showWaiting)}
                                        style={{
                                            background: 'none',
                                            border: '1px solid #ddd',
                                            borderRadius: 6,
                                            padding: '6px 14px',
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            color: '#666',
                                        }}
                                    >
                                        {showWaiting
                                            ? "Amagar a l'espera"
                                            : `Mostrar a l'espera (${waitingCount})`}
                                    </button>
                                ) : null}
                                {closedCount > 0 ? (
                                    <button
                                        onClick={() => setShowClosed(!showClosed)}
                                        style={{
                                            background: 'none',
                                            border: '1px solid #ddd',
                                            borderRadius: 6,
                                            padding: '6px 14px',
                                            fontSize: 13,
                                            cursor: 'pointer',
                                            color: '#666',
                                        }}
                                    >
                                        {showClosed
                                            ? 'Amagar fora de termini'
                                            : `Mostrar fora de termini (${closedCount})`}
                                    </button>
                                ) : null}
                            </div>
                        </div>

                        {visibleGrants.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: 40,
                                color: '#888',
                                fontSize: 15,
                            }}>
                                No hi ha subvencions obertes o pendents en aquest moment.
                            </div>
                        ) : null}

                        {visibleGrants.map(grant => (
                            <SubvencionsGrantCard
                                key={grant.slug}
                                grant={grant}
                                color={grantColors[grant.slug]}
                                isSelected={selectedGrant?.slug === grant.slug}
                                onSelect={() => handleSelectGrant(grant)}
                            />
                        ))}
                    </div>

                    {fetchedAt ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '20px 0',
                            fontSize: 12,
                            color: '#aaa',
                        }}>
                            Dades actualitzades: {new Date(fetchedAt).toLocaleString('ca-ES')}
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

export default SubvencionsApp;
